import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Button variant definitions using CVA
 * Updated to match Figma design system exactly with Tailwind classes
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 relative overflow-hidden cursor-pointer',
  {
    variants: {
      variant: {
        // Primary: Teal background with hover/active states (Figma colors)
        primary: 'bg-button-primary text-button-text border border-transparent hover:bg-button-primary-hover active:bg-button-primary-active',
        // Secondary: Transparent with teal border and text
        secondary: 'bg-transparent border border-button-secondary text-button-secondary hover:bg-button-primary-hover hover:text-button-text hover:border-button-primary-hover active:bg-button-primary-active active:text-button-text active:border-button-primary-active',
        // Tertiary: Light background with dark text (consistent across all sizes)
        tertiary: 'bg-button-tertiary text-button-text border border-transparent hover:bg-button-tertiary-hover active:bg-button-tertiary-hover',
        // Outline: DISREGARDED FOR NOW
        outline: 'bg-transparent border border-white/10 text-white hover:border-white/10 active:border-white/10',
        // Legacy variants (kept for backward compatibility)
        ghost: 'bg-transparent text-accent border border-transparent hover:bg-accent/10',
        destructive: 'bg-error text-white border border-transparent hover:bg-error-dark',
        success: 'bg-success text-white border border-transparent hover:bg-success-dark',
      },
      size: {
        // Large: h-12 (48px), p-4 (16px all sides), text-lg (18px), gap-2 (8px), font-medium, tracking-[0.18px]
        lg: 'h-12 p-4 text-lg font-medium gap-2 tracking-[0.18px]',
        // Medium (Base/Default): h-10 (40px), p-3 (12px all sides), text-base (16px), gap-1 (4px), font-medium, tracking-[0.16px]
        md: 'h-10 p-3 text-base font-medium gap-1 tracking-[0.16px]',
        // Small: h-9 (36px), p-2 (8px all sides), text-sm (14px), gap-1 (4px), font-normal
        sm: 'h-9 p-2 text-sm font-normal gap-1',
        // Legacy sizes (kept for backward compatibility)
        xs: 'text-xs py-1 px-2.5 gap-1',
        xl: 'text-lg md:text-xl py-3 px-6 md:py-4 md:px-8 gap-2.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md', // Medium is the default size throughout the site
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

/**
 * Accordion variant definitions using CVA
 */
export const accordionVariants = cva('', {
  variants: {
    variant: {
      default: 'divide-y divide-muted/30',
      bordered: 'border border-muted/30 rounded-lg divide-y divide-muted/30',
      separated: 'space-y-2',
      'case-study': 'space-y-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type AccordionVariants = VariantProps<typeof accordionVariants>;

/**
 * Tabs container variant definitions using CVA
 */
export const tabsContainerVariants = cva('relative p-1', {
  variants: {
    variant: {
      default: 'bg-surface border border-muted/30 rounded-lg',
      pills: 'bg-surface border border-muted/30 rounded-full',
      underline: 'border-b border-b-muted/30 pb-px',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type TabsContainerVariants = VariantProps<typeof tabsContainerVariants>;

/**
 * Tab button size definitions using CVA
 */
export const tabButtonSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'py-1.5 px-3 text-sm',
      md: 'py-2 px-4 text-base',
      lg: 'py-2.5 px-5 text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type TabButtonSizeVariants = VariantProps<typeof tabButtonSizeVariants>;

/**
 * Tabs variant definitions (for compatibility)
 */
export const tabsVariants = tabsContainerVariants;
export type TabsVariants = TabsContainerVariants;

/**
 * Tab indicator variant definitions
 */
export const tabIndicatorVariants = cva('absolute', {
  variants: {
    variant: {
      default: 'inset-0 bg-accent rounded-lg',
      pills: 'inset-0 bg-accent rounded-full',
      underline: 'bottom-[-1px] left-0 right-0 h-[2px] bg-accent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type TabIndicatorVariants = VariantProps<typeof tabIndicatorVariants>;

/**
 * Tab text color variant definitions
 */
export const tabTextColorVariants = cva('', {
  variants: {
    variant: {
      default: '',
      pills: '',
      underline: '',
    },
    active: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'underline',
      active: true,
      class: 'text-accent',
    },
    {
      variant: 'underline',
      active: false,
      class: 'text-muted hover:text-white',
    },
    {
      variant: ['default', 'pills'],
      active: true,
      class: 'text-primary',
    },
    {
      variant: ['default', 'pills'],
      active: false,
      class: 'text-muted hover:text-white',
    },
  ],
});

export type TabTextColorVariants = VariantProps<typeof tabTextColorVariants>;

/**
 * Card variant definitions using CVA
 */
export const cardVariants = cva('rounded-lg overflow-hidden', {
  variants: {
    variant: {
      default: 'bg-surface/20',
      bordered: 'bg-surface/20 border border-surface/40',
      elevated: 'bg-surface/20 shadow-lg',
      flat: 'bg-surface/10',
    },
    interactive: {
      true: 'cursor-pointer hover:bg-surface/30 transition-colors',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    interactive: false,
  },
});

export type CardVariants = VariantProps<typeof cardVariants>;

